/**
 * Board geometry, parameterised by variant.
 *
 * Every function here derives its bounds from the VariantSpec, so the same
 * code serves 8x8, 10x10 and (later) 12x12. Nothing in this file may assume
 * a board size.
 */
/** Convert a square number (1..numSquares) to [row, col], 0-indexed. */
export function squareToRC(v, square) {
    const perRow = v.boardSize / 2;
    const idx = square - 1;
    const row = Math.floor(idx / perRow);
    const colWithinRow = idx % perRow;
    // Even rows have dark squares on odd columns; odd rows on even columns.
    const col = row % 2 === 0 ? colWithinRow * 2 + 1 : colWithinRow * 2;
    return [row, col];
}
/** Convert [row, col] to a square number, or null if off-board or light. */
export function rcToSquare(v, row, col) {
    if (row < 0 || row >= v.boardSize || col < 0 || col >= v.boardSize)
        return null;
    if ((row + col) % 2 === 0)
        return null; // light square, not playable
    return row * (v.boardSize / 2) + Math.floor(col / 2) + 1;
}
/** The row a man of `color` must reach to be promoted. */
export function promotionRow(v, color) {
    return color === "w" ? 0 : v.boardSize - 1;
}
export function opponent(color) {
    return color === "w" ? "b" : "w";
}
export function emptyBoard(v) {
    return new Array(v.numSquares).fill(null);
}
/**
 * Standard starting position: `startRows` rows of black men at the top,
 * `startRows` rows of white men at the bottom, middle rows empty.
 * International: 20 men each on 1..20 and 31..50.
 */
export function initialBoard(v) {
    const board = emptyBoard(v);
    const perRow = v.boardSize / 2;
    const menPerSide = v.startRows * perRow;
    for (let sq = 1; sq <= menPerSide; sq++) {
        board[sq - 1] = { color: "b", king: false };
    }
    for (let sq = v.numSquares - menPerSide + 1; sq <= v.numSquares; sq++) {
        board[sq - 1] = { color: "w", king: false };
    }
    return board;
}
export function getPiece(board, square) {
    return board[square - 1] ?? null;
}
export function setPiece(board, square, piece) {
    board[square - 1] = piece;
}
export function cloneBoard(board) {
    return board.map((p) => (p ? { ...p } : null));
}
/** Deterministic key of a position (board + side to move) for repetition. */
export function positionKey(board, side) {
    let s = side + ":";
    for (let i = 0; i < board.length; i++) {
        const p = board[i];
        if (!p)
            s += ".";
        else if (p.color === "w")
            s += p.king ? "W" : "w";
        else
            s += p.king ? "B" : "b";
    }
    return s;
}
export function countPieces(board, color) {
    let n = 0;
    for (const p of board)
        if (p && p.color === color)
            n++;
    return n;
}
/** Men/kings breakdown for one colour — needed by the FMJD endgame rules. */
export function countByType(board, color) {
    let men = 0;
    let kings = 0;
    for (const p of board) {
        if (!p || p.color !== color)
            continue;
        if (p.king)
            kings++;
        else
            men++;
    }
    return { men, kings, total: men + kings };
}
/**
 * Square number to alphanumeric notation, e.g. 10x10 square 46 -> "a1".
 * Used for display and PDN export; the internal notation is numeric.
 */
export function squareToAlpha(v, sq) {
    const [row, col] = squareToRC(v, sq);
    return String.fromCharCode(97 + col) + (v.boardSize - row);
}
